import React, {memo, useState} from 'react';
import {Handle, Position} from 'reactflow';

import {Box, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, ChatBotIcon, HeadsetIcon, PhoneCallIcon, PlayIcon} from "@hugeicons/core-free-icons";
import {useSetAtom} from "jotai";
import {ChatDrawerAtom, StartCallAtom} from "../pages/project/atoms";
import {getPalette, hexToRgba, NODE_RADIUS, NODE_SHADOW} from "./nodeTheme";

const START_MENU_ITEMS = [
  {key: "chatbot", label: "Chatbot", description: "Open the chat test drawer", icon: ChatBotIcon},
  {key: "web", label: "Web Call", description: "Talk to it over your mic", icon: HeadsetIcon},
  {key: "phone", label: "Phone Call", description: "Ring a real phone number", icon: PhoneCallIcon},
];

function StartNode({id, selected}) {
  const setChatDrawer = useSetAtom(ChatDrawerAtom);
  const setStartCall = useSetAtom(StartCallAtom);
  const palette = getPalette("start");
  const [anchorEl, setAnchorEl] = useState(null);

  const openMenu = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closeMenu = () => setAnchorEl(null);

  const onSelect = (key) => {
    closeMenu();
    if (key === "chatbot") {
      setChatDrawer({open: true, startNode: id});
    } else {
      setStartCall({open: true, mode: key});
    }
  };

  return (
    <Box className={"node start-node"}
         sx={{
           display: "inline-flex",
           alignItems: "center",
           gap: 0.5,
           bgcolor: palette.accent,
           pl: 0.5, pr: 1, py: 0.5,
           borderRadius: `${NODE_RADIUS}px`,
           border: "1px solid",
           borderColor: palette.border,
           boxShadow: selected ? `${NODE_SHADOW}, 0 0 0 3px ${hexToRgba(palette.accent, 0.3)}` : NODE_SHADOW,
           transition: "box-shadow .18s ease, transform .18s ease",
         }}
    >
      <Stack
        direction={"row"} gap={0.75} alignItems={"center"}
        onClick={openMenu}
        sx={{
          color: "#FFF",
          cursor: "pointer",
          borderRadius: "999px",
          pl: 0.25, pr: 1, py: 0.25,
          background: hexToRgba("#FFFFFF", anchorEl ? 0.22 : 0.14),
          transition: "background-color .16s ease, transform .12s ease",
          "&:hover": {background: hexToRgba("#FFFFFF", 0.26), transform: "translateY(-0.5px)"},
          "&:active": {transform: "translateY(0)"},
        }}
      >
        <Box sx={{
          width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center",
          background: `linear-gradient(145deg, ${hexToRgba("#FFFFFF", 0.32)}, ${hexToRgba("#FFFFFF", 0.12)})`,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
        }}>
                    <HugeiconsIcon icon={PlayIcon} size={11} strokeWidth={2}/>
        </Box>
        <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase"}}>
          Start
        </Typography>
        <HugeiconsIcon icon={ArrowDown01Icon} size={13} strokeWidth={2.2} style={{opacity: 0.85, transform: anchorEl ? "rotate(180deg)" : "none", transition: "transform .16s ease"}}/>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
        transformOrigin={{vertical: "top", horizontal: "left"}}
        PaperProps={{sx: {borderRadius: 3, mt: 0.75, minWidth: 220, overflow: "hidden"}}}
      >
        {START_MENU_ITEMS.map(({key, label, description, icon: Icon}) => (
          <MenuItem key={key} onClick={() => onSelect(key)} sx={{py: 1.1, px: 1.75, gap: 0.5}}>
            <ListItemIcon sx={{minWidth: 34}}>
              <Box sx={{
                width: 30, height: 30, borderRadius: "9px", display: "grid", placeItems: "center",
                bgcolor: hexToRgba(palette.accent, 0.12), color: palette.accent,
              }}>
                <HugeiconsIcon icon={Icon} size={17} strokeWidth={1.8}/>
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={label}
              secondary={description}
              primaryTypographyProps={{fontSize: 13.5, fontWeight: 600}}
              secondaryTypographyProps={{fontSize: 11.5}}
            />
          </MenuItem>
        ))}
      </Menu>

      <Handle type="source" position={Position.Right} id={id}/>
    </Box>
  );
}

export default memo(StartNode);
